import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, CheckCircle2, XCircle, Eye, Banknote, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";

function AuthedImage({ packId, kind, alt, testid }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let alive = true;
    let obj = "";
    (async () => {
      const token = localStorage.getItem("nc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/fab/packs/${packId}/image/${kind}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok && alive) {
        obj = URL.createObjectURL(await res.blob());
        setUrl(obj);
      }
    })();
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [packId, kind]);
  if (!url) return <span className="block w-full aspect-square bg-white/5 animate-pulse" />;
  return <img data-testid={testid} src={url} alt={alt} className="w-full aspect-square object-cover border border-white/10" loading="lazy" />;
}

export default function AdminFabPacks() {
  const [packs, setPacks] = useState([]);
  const [access, setAccess] = useState([]);
  const [emailReport, setEmailReport] = useState(null);
  const [showAccess, setShowAccess] = useState(false);
  const [status, setStatus] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [notes, setNotes] = useState("");
  const [bounties, setBounties] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/fab/packs", { params: { status } }).then((r) => setPacks(r.data)).catch(() => {});
    api.get("/admin/fab/access").then((r) => setAccess(r.data)).catch(() => {});
    api.get("/admin/fab/email-report").then((r) => setEmailReport(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(ok || r.data?.message || "Done");
      setRejectFor(null); setNotes("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = (s) => s === "approved" ? "text-nc-green border-nc-green/40 bg-nc-green/10" : s === "rejected" ? "text-red-400 border-red-500/40 bg-red-500/10" : "text-nc-orange border-nc-orange/40 bg-nc-orange/10";

  return (
    <section data-testid="admin-fabpacks-section" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
          <Package className="w-4 h-4 text-nc-orange" /> Fab Pack QA
        </h2>
        <select data-testid="fabqa-status-filter" className={`${inputCls} max-w-[180px]`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      <div className="p-6 flex flex-col gap-4">
        <div className="border border-white/10 bg-[#050505]/50">
          <button data-testid="fabqa-access-toggle" onClick={() => setShowAccess((s) => !s)} className="w-full px-4 py-3 flex items-center justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 hover:text-zinc-100 transition-colors">
            <span>Contractor access — invite-only ({access.filter((d) => d.invited).length} invited)</span>
            <span className="text-nc-blue">{showAccess ? "Hide" : "Manage"}</span>
          </button>
          {showAccess && (
            <div data-testid="fabqa-access-list" className="border-t border-white/10 p-4 flex flex-col gap-2 max-h-80 overflow-y-auto">
              <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">Only invited devs can see the Fab Packs tab, read the Pack Bible, or submit. ID verification is still required on top — inviting an unverified dev does nothing until their ID clears.</p>
              <div className="flex items-center justify-between gap-3 border border-nc-orange/30 bg-nc-orange/5 px-3 py-2">
                <p className="font-mono text-[9px] text-zinc-400 leading-relaxed">Invited someone who never got the email? Re-queue every invite through the reliable paced outbox (one send every ~31s — nothing gets dropped).</p>
                <button data-testid="fabqa-resend-all" disabled={busy} onClick={() => act(() => api.post("/admin/fab/access/resend-all", {}), null)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 transition-colors shrink-0 disabled:opacity-40">
                  Resend all missing invites
                </button>
              </div>
              {emailReport && (
                <div data-testid="fabqa-email-report" className="border border-white/10 bg-black/40 px-3 py-2.5">
                  <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-400 mb-2">Invite delivery report</p>
                  <div className="flex gap-2 flex-wrap">
                    {[["sent", "text-nc-green border-nc-green/40 bg-nc-green/10"], ["pending", "text-nc-blue border-nc-blue/40 bg-nc-blue/10"], ["sending", "text-nc-blue border-nc-blue/40 bg-nc-blue/10"], ["failed", "text-red-400 border-red-500/40 bg-red-500/10"]].map(([s, cls]) => (
                      <span key={s} data-testid={`fabqa-report-${s}`} className={`font-mono text-[9px] tracking-[0.15em] uppercase border px-2 py-1 ${cls}`}>
                        {s}: {emailReport.counts[s] || 0}
                      </span>
                    ))}
                  </div>
                  {(emailReport.failed || []).length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto flex flex-col gap-1">
                      {emailReport.failed.map((f, k) => (
                        <p key={k} data-testid={`fabqa-report-failed-${k + 1}`} className="font-mono text-[9px] text-red-300 leading-relaxed">
                          ✕ {f.to} — {(f.error || "undeliverable").slice(0, 90)}
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="font-mono text-[8px] text-zinc-600 mt-1.5 leading-relaxed">Pending invites send one every ~31s with automatic retries. Hard-bounced addresses get their access auto-revoked daily and show a red bounced flag below.</p>
                </div>
              )}
              {access.map((d, i) => (
                <div key={d.id} data-testid={`fabqa-access-row-${i + 1}`} className="flex items-center justify-between gap-3 border border-white/10 bg-black/40 px-3 py-2">
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] text-zinc-200 truncate">{d.legal_name || d.name || d.email} <span className="text-zinc-600">// {d.email}</span></p>
                    <p className={`font-mono text-[9px] uppercase tracking-[0.15em] ${d.id_verified ? "text-nc-green" : "text-nc-orange"}`}>{d.id_verified ? "ID verified" : "ID not verified — access stays dormant"}</p>
                    {d.bounced && <p data-testid={`fabqa-bounced-${i + 1}`} className="font-mono text-[9px] uppercase tracking-[0.15em] text-red-400">Bounced — invite email undeliverable, access auto-revoked</p>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {d.invited && (
                      <button
                        data-testid={`fabqa-resend-${i + 1}`}
                        disabled={busy}
                        onClick={() => act(() => api.post("/admin/fab/access", { user_id: d.id, allow: true, resend: true }))}
                        className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border border-nc-blue/40 text-nc-blue hover:bg-nc-blue/10 transition-colors disabled:opacity-40"
                      >
                        Resend email
                      </button>
                    )}
                    <button
                      data-testid={`fabqa-access-btn-${i + 1}`}
                      disabled={busy}
                      onClick={() => act(() => api.post("/admin/fab/access", { user_id: d.id, allow: !d.invited }))}
                      className={`font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border transition-colors shrink-0 disabled:opacity-40 ${d.invited ? "border-red-400/40 text-red-400 hover:bg-red-400/10" : "border-nc-green/40 text-nc-green hover:bg-nc-green/10"}`}
                    >
                      {d.invited ? "Revoke" : "Invite"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
          Character packs submitted by verified contractors for Fab.com sale. Inspect the images against the technical questionnaire — previews must match the real asset counts. Approving credits the AUD 10 bounty and takes the pack as 100% Specpixel property.
        </p>
        {packs.length === 0 && <p data-testid="fabqa-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">No pack submissions.</p>}
        {packs.map((p, i) => (
          <div key={p.id} data-testid={`fabqa-pack-${i + 1}`} className="border border-white/10 bg-[#050505]/60 p-5 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap min-w-0">
                <p className="font-sans font-bold text-sm text-zinc-100">{p.title}</p>
                <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${statusBadge(p.status)}`}>{p.status}</span>
                <span className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.15em]">{p.category}</span>
              </div>
              <p className="font-mono text-[10px] text-zinc-500">{p.legal_name || p.user_email} // {String(p.created_at).slice(0, 16).replace("T", " ")} // bounty AUD {Number(p.bounty_aud).toFixed(2)}</p>
            </div>
            <p className="font-mono text-[10px] text-zinc-400 leading-relaxed whitespace-pre-wrap">{p.concept}</p>
            <p className="font-mono text-[10px] text-zinc-500">
              Tools: {p.ai_tools_used} // UE {p.ue_version} // Epic skeleton {p.epic_skeleton ? "yes" : "NO"} // separate parts {p.separate_parts ? "yes" : "NO"} // Nanite {p.nanite_enabled ? "yes" : "NO"}
              {" // "}<a data-testid={`fabqa-link-${i + 1}`} href={p.delivery_link} target="_blank" rel="noopener noreferrer" className="text-nc-blue underline inline-flex items-center gap-1">delivery link <ExternalLink className="w-3 h-3" /></a>
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 font-mono text-[10px]">
              {[["Unique meshes", p.tech.unique_meshes], ["Skeletal", p.tech.skeletal_meshes], ["Static", p.tech.static_meshes], ["Materials", p.tech.material_count], ["Instances", p.tech.material_instance_count], ["Textures", p.tech.texture_count], ["Resolutions", p.tech.texture_resolutions], ["Tris (assembled)", p.tech.triangles_assembled], ["LODs", p.tech.lod_count], ["Animations", p.tech.animation_count]].map(([label, val]) => (
                <div key={label} className="border border-white/10 bg-black/40 px-2 py-1.5">
                  <p className="text-[8px] tracking-[0.2em] text-zinc-600 uppercase">{label}</p>
                  <p className="text-zinc-200 mt-0.5">{val}</p>
                </div>
              ))}
            </div>
            <button data-testid={`fabqa-expand-${i + 1}`} onClick={() => setExpanded(expanded === p.id ? null : p.id)} className="self-start font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue hover:text-zinc-100 flex items-center gap-1.5 transition-colors">
              <Eye className="w-3.5 h-3.5" /> {expanded === p.id ? "Hide" : "Inspect"} thumbnail + {p.preview_count} previews
            </button>
            {expanded === p.id && (
              <div data-testid={`fabqa-gallery-${i + 1}`} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <AuthedImage packId={p.id} kind="thumb" alt="thumbnail" testid={`fabqa-img-thumb-${i + 1}`} />
                {Array.from({ length: p.preview_count }, (_, k) => (
                  <AuthedImage key={k} packId={p.id} kind={`p${k + 1}`} alt={`preview ${k + 1}`} testid={`fabqa-img-p${k + 1}-${i + 1}`} />
                ))}
              </div>
            )}
            {p.status === "submitted" && (
              <div className="flex gap-2 flex-wrap items-center border-t border-white/10 pt-3">
                <input data-testid={`fabqa-bounty-${i + 1}`} type="number" min="0" className={`${inputCls} w-28`} placeholder="10" value={bounties[p.id] ?? ""} onChange={(e) => setBounties((b) => ({ ...b, [p.id]: e.target.value }))} />
                <button data-testid={`fabqa-approve-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/admin/fab/packs/${p.id}/review`, { action: "approve", notes: "", bounty_aud: parseFloat(bounties[p.id]) || 10 }), "Pack approved — bounty credited")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 bg-nc-green hover:bg-nc-green/80 text-white transition-colors flex items-center gap-1.5 disabled:opacity-40">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve (AUD {parseFloat(bounties[p.id]) || 10})
                </button>
                <button data-testid={`fabqa-reject-${i + 1}`} disabled={busy} onClick={() => setRejectFor(rejectFor === p.id ? null : p.id)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors flex items-center gap-1.5">
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            )}
            {p.status === "approved" && (
              <div className="flex gap-2 items-center border-t border-white/10 pt-3">
                <button data-testid={`fabqa-paid-${i + 1}`} disabled={busy} onClick={() => act(() => api.post(`/admin/fab/packs/${p.id}/mark-paid`), "Bounty marked paid")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-green/40 text-nc-green hover:bg-nc-green/10 transition-colors flex items-center gap-1.5">
                  <Banknote className="w-3.5 h-3.5" /> Mark bounty paid
                </button>
                {p.review_notes && <p className="font-mono text-[10px] text-zinc-500">Notes: {p.review_notes}</p>}
              </div>
            )}
            {p.status === "rejected" && p.review_notes && <p className="font-mono text-[10px] text-red-300 border-l-2 border-red-500/50 pl-3">Rejected: {p.review_notes}</p>}
            {rejectFor === p.id && (
              <div className="border border-red-500/30 bg-red-500/5 p-4 flex flex-col gap-3">
                <p className="font-mono text-[10px] tracking-[0.2em] text-red-400 uppercase">QA notes for the contractor (required — tell them exactly what to fix)</p>
                <textarea data-testid={`fabqa-reject-notes-${i + 1}`} rows={3} className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Not on the Epic Skeleton — hand bones renamed. Previews 3-4 show armor detail not present in the textures." />
                <div className="flex gap-2">
                  <button data-testid={`fabqa-reject-confirm-${i + 1}`} disabled={busy || notes.trim().length < 10} onClick={() => act(() => api.post(`/admin/fab/packs/${p.id}/review`, { action: "reject", notes }), "Pack rejected with notes")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-40">
                    Confirm Reject
                  </button>
                  <button onClick={() => { setRejectFor(null); setNotes(""); }} className="font-mono text-[9px] uppercase px-3 py-2 text-zinc-400">Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
